import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import {
    AddRounded,
    ContentCopyRounded,
    DeleteOutlineRounded,
    EditRounded,
    HistoryRounded,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import { Button, IconButton } from "../components/Buttons";
import {
    createWebhook,
    deleteWebhook,
    listWebhookDeliveries,
    listWebhooks,
    updateWebhook,
} from "../api";
import { formatDate, formatDateTime } from "../utils/format";
import { apiError } from "../utils/meta";

const MONO = "'SF Mono', Menlo, monospace";

const EVENTS = [
    { key: "record.created", label: "Record created" },
    { key: "record.updated", label: "Record updated" },
    { key: "record.stage_changed", label: "Record changes stage" },
];

const eventLabel = (key) => EVENTS.find((e) => e.key === key)?.label ?? key;

async function copyToClipboard(text, label) {
    try {
        await navigator.clipboard.writeText(text);
        toast.success(`${label} copied`);
    } catch {
        toast.error("Could not copy — select and copy it manually");
    }
}

/* ------------------------------------------------------------------ */
/* Create / edit dialog                                                 */
/* ------------------------------------------------------------------ */

function WebhookDialog({ open, webhook, onClose, onSaved }) {
    const isEdit = !!webhook;
    const [url, setUrl] = useState("");
    const [events, setEvents] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setUrl(webhook?.url ?? "");
        setEvents(webhook?.events ?? []);
    }, [open, webhook]);

    const toggleEvent = (key) =>
        setEvents((prev) =>
            prev.includes(key)
                ? prev.filter((e) => e !== key)
                : [...prev, key],
        );

    async function handleSave() {
        if (!/^https?:\/\//.test(url.trim())) {
            toast.error("Enter a valid http(s) URL");
            return;
        }
        if (events.length === 0) {
            toast.error("Pick at least one event");
            return;
        }
        setBusy(true);
        try {
            if (isEdit) {
                await updateWebhook(webhook.id, { url: url.trim(), events });
                toast.success("Webhook updated");
                onSaved(null);
            } else {
                const { data } = await createWebhook({
                    url: url.trim(),
                    events,
                });
                onSaved(data?.secret ?? null);
            }
        } catch (err) {
            toast.error(apiError(err, "Could not save the webhook"));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontFamily: "'Newsreader', Georgia, serif" }}>
                {isEdit ? "Edit webhook" : "New webhook"}
            </DialogTitle>
            <DialogContent>
                <Stack sx={{ gap: 2, pt: 1 }}>
                    <TextField
                        label="Endpoint URL"
                        placeholder="https://example.com/hooks/tessera"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        required
                        fullWidth
                        autoFocus
                    />
                    <Stack sx={{ gap: 0.5 }}>
                        <Typography
                            sx={{
                                fontSize: 12,
                                letterSpacing: "1.5px",
                                fontWeight: 700,
                                color: "var(--text-strong-muted)",
                            }}
                        >
                            EVENTS
                        </Typography>
                        {EVENTS.map((ev) => (
                            <Stack
                                key={ev.key}
                                onClick={() => toggleEvent(ev.key)}
                                sx={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: 0.5,
                                    cursor: "pointer",
                                    userSelect: "none",
                                }}
                            >
                                <Checkbox
                                    size="small"
                                    checked={events.includes(ev.key)}
                                    sx={{
                                        p: 0.5,
                                        color: "var(--border-strong)",
                                        "&.Mui-checked": {
                                            color: "var(--accent)",
                                        },
                                    }}
                                />
                                <Typography sx={{ fontSize: 14 }}>
                                    {ev.label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        fontFamily: MONO,
                                        color: "var(--text-tertiary)",
                                        ml: 0.5,
                                    }}
                                >
                                    {ev.key}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button variant="soft-outlined" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    loading={busy}
                    onClick={handleSave}
                >
                    {isEdit ? "Save changes" : "Create webhook"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* ------------------------------------------------------------------ */
/* One-time signing secret                                              */
/* ------------------------------------------------------------------ */

function SecretRevealDialog({ secret, onClose }) {
    return (
        <Dialog open={!!secret} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontFamily: "'Newsreader', Georgia, serif" }}>
                Signing secret
            </DialogTitle>
            <DialogContent>
                <Stack sx={{ gap: 2, pt: 0.5 }}>
                    <Typography
                        sx={{ fontSize: 14, color: "var(--text-secondary)" }}
                    >
                        Copy it now — it is never shown again. Verify each
                        delivery by checking{" "}
                        <code>X-Tessera-Signature</code>: the HMAC-SHA256 of the
                        raw request body with this secret.
                    </Typography>
                    <Stack
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1,
                            background: "var(--bg-tint-soft)",
                            border: "1px solid var(--border-mid)",
                            borderRadius: "3px",
                            p: "10px 14px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: MONO,
                                fontSize: 13,
                                wordBreak: "break-all",
                                flex: 1,
                            }}
                        >
                            {secret}
                        </Typography>
                        <IconButton
                            size="32px"
                            variant="text"
                            title="Copy secret"
                            onClick={() =>
                                copyToClipboard(secret, "Signing secret")
                            }
                        >
                            <ContentCopyRounded sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Stack>
                    <Typography sx={{ fontSize: 13, color: "var(--warning)" }}>
                        This is the only time the secret is visible.
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button variant="contained" color="primary" onClick={onClose}>
                    I've copied it
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* ------------------------------------------------------------------ */
/* Deliveries drawer                                                    */
/* ------------------------------------------------------------------ */

function DeliveriesDrawer({ webhook, onClose }) {
    const open = !!webhook;
    const [deliveries, setDeliveries] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!webhook) return;
        (async () => {
            setLoading(true);
            try {
                const { data } = await listWebhookDeliveries(webhook.id);
                setDeliveries(Array.isArray(data) ? data : (data?.data ?? []));
            } catch (err) {
                toast.error(apiError(err, "Failed to load deliveries"));
            } finally {
                setLoading(false);
            }
        })();
    }, [webhook]);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: { xs: "100%", md: 520 }, background: "var(--bg)" },
            }}
        >
            <Stack sx={{ height: "100%" }}>
                <Stack
                    sx={{
                        p: "24px 28px 20px",
                        borderBottom: "1px solid var(--border)",
                        gap: 0.5,
                        background: "var(--bg-secondary)",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 11,
                            letterSpacing: "2px",
                            fontWeight: 700,
                            color: "var(--text-tertiary)",
                        }}
                    >
                        RECENT DELIVERIES
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: MONO,
                            fontSize: 14,
                            wordBreak: "break-all",
                        }}
                    >
                        {webhook?.url}
                    </Typography>
                </Stack>

                <Stack
                    sx={{ flex: 1, overflowY: "auto", p: "20px 28px", gap: 1.25 }}
                >
                    {loading && (
                        <Stack sx={{ alignItems: "center", py: 6 }}>
                            <CircularProgress size={26} />
                        </Stack>
                    )}
                    {!loading && deliveries.length === 0 && (
                        <Typography
                            sx={{
                                fontSize: 14,
                                color: "var(--text-secondary)",
                                textAlign: "center",
                                py: 6,
                            }}
                        >
                            No deliveries yet — this webhook hasn't fired.
                        </Typography>
                    )}
                    {!loading &&
                        deliveries.map((d) => {
                            const ok =
                                d.statusCode >= 200 && d.statusCode < 300;
                            const color = ok
                                ? "var(--success)"
                                : "var(--error)";
                            return (
                                <Stack
                                    key={d.id}
                                    sx={{
                                        border: "1px solid var(--border-mid)",
                                        borderRadius: "4px",
                                        background: "var(--bg-secondary)",
                                        p: "12px 14px",
                                        gap: 0.75,
                                    }}
                                >
                                    <Stack
                                        sx={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 1.5,
                                        }}
                                    >
                                        <Stack
                                            sx={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                                gap: 1.5,
                                            }}
                                        >
                                            <Chip
                                                size="small"
                                                label={d.statusCode ?? "—"}
                                                sx={{
                                                    height: 22,
                                                    fontSize: 11.5,
                                                    fontWeight: 600,
                                                    fontFamily: MONO,
                                                    color,
                                                    background: "transparent",
                                                    border: `1px solid ${color}`,
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    fontFamily: MONO,
                                                }}
                                            >
                                                {d.event}
                                            </Typography>
                                        </Stack>
                                        <Typography
                                            sx={{
                                                fontSize: 12.5,
                                                color: "var(--text-secondary)",
                                            }}
                                        >
                                            {formatDateTime(d.createdAt)}
                                        </Typography>
                                    </Stack>
                                    {d.error && (
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                fontFamily: MONO,
                                                color: "var(--error)",
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {d.error}
                                        </Typography>
                                    )}
                                </Stack>
                            );
                        })}
                </Stack>
            </Stack>
        </Drawer>
    );
}

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

export default function AdminWebhooks() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialog, setDialog] = useState({ open: false, webhook: null });
    const [secret, setSecret] = useState(null);
    const [deliveriesFor, setDeliveriesFor] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [deleteBusy, setDeleteBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await listWebhooks();
            setRows(Array.isArray(data) ? data : (data?.data ?? []));
        } catch (err) {
            toast.error(apiError(err, "Failed to load webhooks"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    async function handleToggle(row) {
        const next = !row.isActive;
        /* Optimistic flip, revert on failure. */
        setRows((prev) =>
            prev.map((r) => (r.id === row.id ? { ...r, isActive: next } : r)),
        );
        try {
            await updateWebhook(row.id, { isActive: next });
        } catch (err) {
            setRows((prev) =>
                prev.map((r) =>
                    r.id === row.id ? { ...r, isActive: row.isActive } : r,
                ),
            );
            toast.error(apiError(err, "Could not toggle the webhook"));
        }
    }

    async function handleDelete() {
        if (!deleting) return;
        setDeleteBusy(true);
        try {
            await deleteWebhook(deleting.id);
            toast.success("Webhook deleted");
            setDeleting(null);
            load();
        } catch (err) {
            toast.error(apiError(err, "Could not delete the webhook"));
        } finally {
            setDeleteBusy(false);
        }
    }

    const columns = useMemo(
        () => [
            {
                key: "url",
                label: "Endpoint",
                render: (row) => (
                    <Typography
                        sx={{
                            fontFamily: MONO,
                            fontSize: 13,
                            wordBreak: "break-all",
                        }}
                    >
                        {row.url}
                    </Typography>
                ),
            },
            {
                key: "events",
                label: "Events",
                render: (row) => (
                    <Stack
                        sx={{
                            flexDirection: "row",
                            flexWrap: "wrap",
                            gap: 0.5,
                        }}
                    >
                        {(row.events ?? []).map((ev) => (
                            <Chip
                                key={ev}
                                size="small"
                                label={eventLabel(ev)}
                                sx={{
                                    height: 20,
                                    fontSize: 11,
                                    background: "var(--bg-tint-soft)",
                                    border: "1px solid var(--border-mid)",
                                    color: "var(--text-secondary)",
                                }}
                            />
                        ))}
                    </Stack>
                ),
            },
            {
                key: "isActive",
                label: "Active",
                render: (row) => (
                    <Switch
                        size="small"
                        checked={!!row.isActive}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleToggle(row)}
                    />
                ),
            },
            {
                key: "createdAt",
                label: "Created",
                render: (row) => formatDate(row.createdAt),
            },
            {
                key: "actions",
                label: "",
                align: "right",
                render: (row) => (
                    <Stack
                        sx={{
                            flexDirection: "row",
                            justifyContent: "flex-end",
                            gap: 0.5,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <IconButton
                            size="32px"
                            variant="text"
                            title="Deliveries"
                            onClick={() => setDeliveriesFor(row)}
                        >
                            <HistoryRounded sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                            size="32px"
                            variant="text"
                            title="Edit"
                            onClick={() =>
                                setDialog({ open: true, webhook: row })
                            }
                        >
                            <EditRounded sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                            size="32px"
                            variant="text"
                            color="error"
                            title="Delete"
                            onClick={() => setDeleting(row)}
                        >
                            <DeleteOutlineRounded sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Stack>
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return (
        <Stack>
            <PageHeader
                title="Webhooks"
                subtitle="Push record events to your own systems the moment they happen"
                action={
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddRounded />}
                        onClick={() => setDialog({ open: true, webhook: null })}
                    >
                        New webhook
                    </Button>
                }
            />

            <DataTable
                columns={columns}
                rows={rows}
                loading={loading}
                emptyMessage="No webhooks yet — add an endpoint to receive record events"
            />

            <WebhookDialog
                open={dialog.open}
                webhook={dialog.webhook}
                onClose={() => setDialog({ open: false, webhook: null })}
                onSaved={(newSecret) => {
                    setDialog({ open: false, webhook: null });
                    if (newSecret) setSecret(newSecret);
                    load();
                }}
            />

            <SecretRevealDialog
                secret={secret}
                onClose={() => setSecret(null)}
            />

            <DeliveriesDrawer
                webhook={deliveriesFor}
                onClose={() => setDeliveriesFor(null)}
            />

            <Dialog open={!!deleting} onClose={() => setDeleting(null)}>
                <DialogTitle
                    sx={{ fontFamily: "'Newsreader', Georgia, serif" }}
                >
                    Delete this webhook?
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{ fontSize: 14.5, color: "var(--text-secondary)" }}
                    >
                        Deliveries to{" "}
                        <code style={{ wordBreak: "break-all" }}>
                            {deleting?.url}
                        </code>{" "}
                        will stop permanently.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        variant="soft-outlined"
                        onClick={() => setDeleting(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        loading={deleteBusy}
                        onClick={handleDelete}
                    >
                        Delete webhook
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
